<template>
  <!-- Mobile header: folder name with back/cancel -->
  <DialogHeader v-if="useMobileLayout">
    <template #left>
      <div
        v-if="!isRootFolder"
        class="flex items-center cursor-pointer"
        @click="handleBackButton"
      >
        <Icon
          :size="25"
          name="ChevronUp"
          class="stroke-gray-800 -rotate-90 -ml-2"
        />
        <div class="font-secondary">Back</div>
      </div>
    </template>

    {{ currentFolderName }}

    <template #right>
      <div class="text-green-900 cursor-pointer" @click="close()">Cancel</div>
    </template>
  </DialogHeader>

  <!-- Desktop header: static title -->
  <DialogHeader v-else>
    Move {{ ids.length }} {{ pluralize('item', ids.length) }}
  </DialogHeader>

  <DialogContent class="flex-1!">
    <!-- Desktop: breadcrumb navigation -->
    <Breadcrumb
      v-if="!useMobileLayout"
      :path="breadcrumbPath"
      :key="breadcrumbKey"
      :iconSize="18"
      class="text-[14px]"
      @location="handleLocationUpdate"
    />

    <div
      :class="
        useMobileLayout ? 'h-full' : 'h-[268px] overflow-y-auto -mx-[30px]'
      "
    >
      <div
        v-if="loading"
        class="w-full h-full flex items-center justify-center"
      >
        <Spinner :size="useMobileLayout ? 16 : 22" />
      </div>
      <Table.Base
        v-else-if="folders.length"
        :class="!useMobileLayout && 'mt-5'"
      >
        <Table.Row
          v-for="folder in folders"
          :key="folder.id"
          :class="[
            useMobileLayout
              ? 'border-gray-800/8'
              : 'first:border-t border-gray-800/8',
            folderClasses(folder),
          ]"
          @click="handleFolderClick(folder)"
        >
          <Table.Cell :class="useMobileLayout ? 'px-[22px]' : 'px-[30px]'">
            <ThumbnailIcon
              :class="[
                useMobileLayout ? 'w-9 h-9 mr-2.5' : 'w-6 h-6 mr-2.5',
                folderCellClasses(folder),
              ]"
              type="folder"
              :icon-size="useMobileLayout ? 36 : 24"
            />
            <div v-truncate class="w-full" :class="folderCellClasses(folder)">
              <div data-truncate-target>{{ folder.name }}</div>
            </div>
          </Table.Cell>
        </Table.Row>
      </Table.Base>
      <div
        v-else
        class="font-secondary w-full h-full flex items-center justify-center text-[14px] opacity-50"
      >
        Move {{ pluralize('item', ids.length) }} here
      </div>
    </div>
  </DialogContent>

  <!-- Mobile footer -->
  <div
    v-if="useMobileLayout"
    class="w-full flex items-center justify-between p-4 font-secondary text-[16px] leading-[22px] text-gray-800 border-t border-[#EFEFEF]"
  >
    <div class="cursor-pointer" @click="handleNewFolderClick">
      Create folder
    </div>
    <div
      v-if="!moveItemsLoading"
      class="font-semibold font-primary text-green-700 cursor-pointer"
      :class="buttonClasses"
      @click="handleClick"
    >
      Save
    </div>
    <Spinner v-else :size="20" />
  </div>

  <!-- Desktop footer -->
  <DialogFooter v-else class="mt-auto!">
    <div class="w-full flex items-center justify-between">
      <div
        class="font-secondary text-[14px] font-medium text-green-700 cursor-pointer"
        :class="disableAction && 'pointer-events-none'"
        @click="handleNewFolderClick"
      >
        Create new folder
      </div>
      <div class="flex items-center">
        <UiButton class="mr-2" :disabled="disableAction" @click="close()">
          Cancel
        </UiButton>
        <UiButton
          :disabled="disableAction"
          :loading="moveItemsLoading"
          primary
          @click="handleClick"
        >
          Move
        </UiButton>
      </div>
    </div>
  </DialogFooter>
</template>

<script setup lang="ts">
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import Breadcrumb from '@/shared/components/Breadcrumb.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { useDevice } from '@/shared/composables/useDevice'
import { Dialogs } from '@/shared/dialogs/types'
import { useMoveItemsMutation } from '@/shared/graphql/legacy/generated'
import { graphql } from '@/shared/graphql/router'
import type { Folder } from '@/shared/graphql/router/graphql'
import { computed, inject, ref } from 'vue'
import { UiButton, ThumbnailIcon, Table, Icon } from '@dacr/ui'
import { removeItemsFromExplorerCache } from '@/features/explorer/utils/explorerCache'
import Spinner from '@/shared/components/Spinner.vue'
import pluralize from 'pluralize'
import { TableTypes } from '@/shared/types/index'
import { useQuery } from '@vue/apollo-composable'

const FOLDERS_QUERY = graphql(`
  query MoveFolders($path: String!) {
    folderByPath(path: $path) {
      id
      name
      path
      items(input: { first: 100, filterBy: { type: FOLDER } }) {
        edges {
          node {
            ... on Folder {
              id
              name
              path
              totalFiles
            }
          }
        }
      }
    }
  }
`)

const CONFIRM_THRESHOLD = 50

const { close, open } = useDialog()
const { isMobile } = useDevice()
const isModal = inject<boolean>('dialogModal', false)
const useMobileLayout = isMobile.value && !isModal

const props = defineProps<{
  ids: string[]
}>()

const table = Table.useTable(TableTypes.Explorer)

const folderPath = ref('/')

const variables = computed(() => ({
  path: folderPath.value,
}))

const { result, loading } = useQuery(FOLDERS_QUERY, variables, {
  fetchPolicy: 'cache-and-network',
  nextFetchPolicy: 'cache-only',
  clientId: 'router',
})

const { mutate: moveItems, loading: moveItemsLoading } = useMoveItemsMutation()

const path = ref<{ name: string; id: string; path: string }[]>([
  { name: 'Dacr', id: 'root', path: '/' },
])

const currentFolder = computed(() => path.value.at(-1)!)
const currentFolderName = computed(() => currentFolder.value.name)
const isRootFolder = computed(() => currentFolder.value.path === '/')
const breadcrumbPath = computed(() => path.value.map((p) => p.name))
const breadcrumbKey = computed(() => breadcrumbPath.value.join('-'))

const folders = computed(
  () =>
    result.value?.folderByPath?.items?.edges
      ?.map((e) => e.node as Folder)
      .filter(Boolean) ?? []
)

const shouldDisableFolder = (folder: Folder) =>
  loading.value || moveItemsLoading.value || props.ids.includes(folder.id)

const folderClasses = (folder: Folder) => ({
  'pointer-events-none': shouldDisableFolder(folder),
})

const folderCellClasses = (folder: Folder) => ({
  'opacity-50': shouldDisableFolder(folder),
})

const disableAction = computed(() => loading.value || moveItemsLoading.value)

const buttonClasses = computed(() => ({
  'opacity-50 pointer-events-none': disableAction.value,
}))

const handleLocationUpdate = (location: string[]) => {
  path.value = path.value.slice(0, location.length)
  folderPath.value = currentFolder.value.path
}

const handleBackButton = () => {
  path.value.pop()
  folderPath.value = currentFolder.value.path
}

const handleFolderClick = (folder: Folder) => {
  path.value.push({ name: folder.name, id: folder.id, path: folder.path })
  folderPath.value = folder.path
}

const handleNewFolderClick = () => {
  const destId = result.value?.folderByPath?.id || 'root'

  open(Dialogs.AddFolder, {
    props: {
      folderId: destId,
    },
    stack: true,
  })
}

const save = async () => {
  const destId = result.value?.folderByPath?.id || currentFolder.value.id

  try {
    const response = await moveItems({
      sourceIds: props.ids,
      to: destId,
    })

    removeItemsFromExplorerCache(props.ids)
    table.resetSelection()

    if (response?.data?.moveItems.asyncProcess) {
      open(Dialogs.Success, {
        props: {
          title: 'Bulk change successful',
          html: 'Note: In some cases, it may take several minutes to process your request.',
        },
      })
    } else {
      close()
    }
  } catch (error) {
    console.error(error)

    if (error instanceof Error) {
      open(Dialogs.Error, {
        props: {
          content: 'Could not move items to specified destination',
          error,
        },
      })
    }
  }
}

const handleClick = async () => {
  if (props.ids.length >= CONFIRM_THRESHOLD) {
    const destFolder = result.value?.folderByPath
    return open(Dialogs.ConfirmMove, {
      props: {
        folder: destFolder,
        ids: props.ids,
        callback: save,
      },
    })
  }

  await save()
}
</script>
